/**
 * Everything about the selected area, on the right (RESP2c).
 *
 * The section that needed the most care is "Zuweisungsregeln". The
 * reference design shows three rules; the backend has one. The other two
 * are not built as switches that would look real and do nothing — a
 * toggle that survives no reload is worse than an absent feature,
 * because it lies about what the system will do.
 *
 * So one rule is shown, driven by `apply_default_on_task_create`, and it
 * is not directly clickable: changing it goes through the edit dialog,
 * which is where every other field is changed too. A control that saves
 * on click beside controls that save on submit is two mental models in
 * one panel.
 *
 * "Verwendung" comes from its own endpoint for THIS area only. While it
 * loads the numbers are skeletons rather than zeros — a zero that turns
 * into 24 reads as a bug, and a zero that stays zero reads as a fact,
 * and the person cannot tell which they are looking at.
 */
import React from "react";
import { Check, ChevronRight, Circle, Pencil } from "lucide-react";

import { Skeleton } from "../../../components/ui/skeleton";
import { formatMinutes } from "../calendar/workReports/workReportConstants";

import { AreaIconTile, StatusBadge } from "./ResponsibilityAreaList";
import { buildAreaPeople, personInitials } from "./areaPeople";

const SECTION =
  "text-[11px] font-semibold uppercase tracking-[0.07em] text-muted-foreground";

/** German label for a warning code. The API sends the CODE. */
export function warningLabel(code) {
  switch (code) {
    case "inactive_default_assignee":
      return "Hauptverantwortliche Person ist deaktiviert";
    case "inactive_participant":
      return "Beteiligte Person ist deaktiviert";
    case "inactive_assignees":
      return "Mehrere zugewiesene Personen sind deaktiviert";
    default:
      return null;
  }
}

function Rule({ enabled, title, subtitle }) {
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className={
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full " +
          (enabled
            ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
            : "bg-muted text-muted-foreground")
        }
      >
        {enabled ? (
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        ) : (
          <Circle className="h-2.5 w-2.5" strokeWidth={3} />
        )}
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-medium text-foreground">
          {title}
        </span>
        <span className="block text-[12px] text-muted-foreground">
          {subtitle}
        </span>
        {/* Not colour alone — the state is also a word, and one that
            cannot be mistaken for the area's own Aktiv/Inaktiv badge. */}
        <span className="sr-only">
          {enabled ? "Regel aktiv" : "Regel nicht aktiv"}
        </span>
      </span>
    </li>
  );
}

function UsageRow({ label, value, loading }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton className="h-4 w-10" />
      ) : (
        <span className="text-[13px] font-semibold text-foreground">
          {value}
        </span>
      )}
    </div>
  );
}

export default function ResponsibilityAreaDetail({
  area,
  usage,
  usageLoading,
  usageError,
  canEdit,
  onEdit,
}) {
  if (!area) {
    return (
      <div className="px-5 py-12 text-center">
        <p className="m-0 text-[13px] text-muted-foreground">
          Wählen Sie einen Bereich aus, um Details zu sehen.
        </p>
      </div>
    );
  }

  const { people, total, kind } = buildAreaPeople(area);
  const warning = warningLabel(area.warning_state);

  return (
    <div className="p-5">
      <div className="flex items-start gap-3">
        <AreaIconTile area={area} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="m-0 truncate text-[16px] font-bold text-foreground">
              {area.label}
            </h2>
            <StatusBadge active={area.is_active} />
          </div>
          <p className="m-0 mt-0.5 text-[12px] text-muted-foreground">
            {area.is_system ? "Systembereich" : "Eigener Tätigkeitsbereich"}
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            onClick={() => onEdit(area)}
            aria-label={`${area.label} bearbeiten`}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {warning ? (
        <p
          role="status"
          className="m-0 mt-4 rounded-[10px] border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] font-medium text-amber-800 dark:text-amber-300"
        >
          {warning}
        </p>
      ) : null}

      <section className="mt-5">
        <h3 className={SECTION}>Beschreibung</h3>
        <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
          {area.description || "Keine Beschreibung hinterlegt"}
        </p>
      </section>

      <section className="mt-5">
        <h3 className={SECTION}>Standardverantwortlicher</h3>
        {kind === "open" ? (
          <p className="m-0 mt-1.5 text-[13px] text-muted-foreground">
            Nicht zugewiesen
          </p>
        ) : (
          <button
            type="button"
            disabled={!canEdit}
            onClick={canEdit ? () => onEdit(area) : undefined}
            className={
              "mt-1.5 flex w-full items-start gap-3 rounded-[10px] border border-border px-3 py-2.5 text-left " +
              (canEdit
                ? "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                : "cursor-default")
            }
          >
            {/* RESP2c.1: EVERY name, because the panel has the room for
                them and "2 Mitglieder" was never the answer to the
                question this page exists to answer. The avatars are the
                same people in the same order — a stack that showed two
                faces beside one name was its own small lie. */}
            <span
              aria-hidden="true"
              className="flex shrink-0 -space-x-2"
            >
              {people.slice(0, 3).map((person) => (
                <span
                  key={person.id || person.label}
                  className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-card bg-muted text-[11px] font-semibold text-foreground"
                >
                  {personInitials(person)}
                </span>
              ))}
            </span>
            <span className="min-w-0 flex-1">
              {people.map((person) => (
                <span
                  key={person.id || person.label}
                  title={person.label}
                  className={
                    "block truncate text-[13px] " +
                    (person.isLead ? "font-medium " : "") +
                    (person.isActive
                      ? "text-foreground"
                      : "text-muted-foreground line-through")
                  }
                >
                  {person.label}
                </span>
              ))}
              {total > 1 ? (
                // Beside the names, never instead of them.
                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                  {`${total} Personen`}
                </span>
              ) : null}
            </span>
            {canEdit ? (
              <ChevronRight
                aria-hidden="true"
                className="mt-1 h-4 w-4 shrink-0 text-muted-foreground"
              />
            ) : null}
          </button>
        )}
      </section>

      <section className="mt-5">
        <h3 className={SECTION}>Zuweisungsregeln</h3>
        <ul className="m-0 mt-2 list-none space-y-3 p-0">
          <Rule
            enabled={area.apply_default_on_task_create !== false}
            title="Bei neuen Aufgaben vorschlagen"
            subtitle="Bereich und Standardpersonen bei neuen Aufgaben vorschlagen"
          />
        </ul>
      </section>

      <section className="mt-5">
        <h3 className={SECTION}>Verwendung</h3>
        {usageError ? (
          <p role="alert" className="m-0 mt-1.5 text-[12px] text-destructive">
            {usageError}
          </p>
        ) : (
          <div className="mt-1 divide-y divide-border">
            <UsageRow
              label="Aktive Aufgaben"
              value={usage?.open_task_count ?? 0}
              loading={usageLoading}
            />
            <UsageRow
              label="Arbeitsrapporte (Monat)"
              value={usage?.current_month_work_report_count ?? 0}
              loading={usageLoading}
            />
            <UsageRow
              label="Zeit (Monat)"
              value={formatMinutes(usage?.current_month_minutes ?? 0)}
              loading={usageLoading}
            />
            <UsageRow
              label="Aufgaben insgesamt"
              value={usage?.task_count_total ?? 0}
              loading={usageLoading}
            />
            <UsageRow
              label="Arbeitsrapporte insgesamt"
              value={usage?.work_report_count_total ?? 0}
              loading={usageLoading}
            />
          </div>
        )}
      </section>

      <section className="mt-5">
        <h3 className={SECTION}>Letzte Änderung</h3>
        {area.updated_at ? (
          <>
            <p className="m-0 mt-1.5 text-[13px] text-foreground">
              {new Date(area.updated_at).toLocaleString("de-CH", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            <p className="m-0 text-[12px] text-muted-foreground">
              {/* `updated_by_user` is a picker projection — a label, never
                  a raw id. Absent means the row was seeded, not edited. */}
              {area.updated_by_user?.label
                ? `von ${area.updated_by_user.label}`
                : "durch System"}
            </p>
          </>
        ) : (
          <p className="m-0 mt-1.5 text-[13px] text-muted-foreground">
            Noch nicht bearbeitet
          </p>
        )}
      </section>
    </div>
  );
}
